import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Select, OutlinedInput, MenuItem, Checkbox, ListItemText, SelectChangeEvent } from "@mui/material";
import { useEffect, useState } from "react";
import { useAuthenticatedFetch } from "../../utils/auth-fetch";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { FormModel } from "../../models/form.model";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export interface FormGroupModel {
  _id?: string;
  title: string;
  description: string;
  forms: FormModel[];
}

export const CreateFormGroupDialog = ({ open, onClose, forms, formGroup }: { open: boolean; onClose: () => void; forms: FormModel[]; formGroup: FormGroupModel | undefined }) => {
  const baseFormGroup = {
    title: "",
    description: "",
    forms: [],
  };
  const [formData, setFormData] = useState<FormGroupModel>({
    title: "",
    description: "",
    forms: [],
  });

  const [selectedForms, setSelectedForms] = useState<string[]>([]);

  const authenticatedFetch = useAuthenticatedFetch();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (formGroup) {
      setFormData(formGroup);
      setSelectedForms(formGroup.forms.map((form) => form._id));
    } else {
      setFormData(Object.assign({}, baseFormGroup));
      setSelectedForms([]);
    }
  }, [formGroup]);

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    const fetchType = formGroup
      ? () =>
        authenticatedFetch(`/api/form-group/${formGroup._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        })
      : () =>
        authenticatedFetch("/api/form-group", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });

    await fetchType().then((res) => {
      if (res.ok) {
        const responseType = formGroup ? "Form group updated successfully" : "Form group created successfully";
        showSnackbar(responseType, "success");
        onClose();
      } else {
        const responseType = formGroup ? "Failed to update form group" : "Failed to create form group";
        showSnackbar(responseType, "error");
      }
    });
  };

  const handleSelectChange = (event: SelectChangeEvent<any>) => {
    const values = event.target.value as string[];
    setSelectedForms(values);

    const formsFiltered = forms.filter((form) => values.includes(form._id));
    const formsOrdered: FormModel[] = [];

    for (const selectedFormId of values) {
      const requiredForm = formsFiltered.find(form => form._id === selectedFormId);
      if (requiredForm) {
        formsOrdered.push(requiredForm);
      }
    }

    setFormData((prev) => ({
      ...prev,
      forms: formsOrdered,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{formGroup ? "Edit" : "Create"} Form Group</DialogTitle>
      <form onSubmit={handleSubmit} className="pw-4">
        <DialogContent>
          <TextField label="Title" type="text" name="title" value={formData.title} onChange={handleChange} required fullWidth margin="normal" />

          <TextField label="Description (optional)" type="text" name="description" value={formData.description} onChange={handleChange} fullWidth margin="normal" />

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Forms</label>
            <Select
              multiple
              value={selectedForms}
              onChange={handleSelectChange}
              input={<OutlinedInput placeholder="Forms" />}
              renderValue={(selected) =>
                forms
                  .filter((form) => selected.includes(form._id))
                  .sort((a, b) => selected.indexOf(a._id) - selected.indexOf(b._id))
                  .map((form) => form.title)
                  .join(", ")
              }
              MenuProps={MenuProps}
              fullWidth
            >
              {forms.map((form) => (
                <MenuItem key={form._id} value={form._id}>
                  <Checkbox checked={selectedForms.includes(form._id)} />
                  <ListItemText primary={form.title} />
                </MenuItem>
              ))}
            </Select>
          </div>
        </DialogContent>
        <DialogActions>
          <button type="button" className="w-36 text-orange-600 py-2 m-2 px-4 rounded float-end" onClick={onClose}>
            Close
          </button>
          <button type="submit" className="w-36 bg-orange-600 text-white py-2 m-2 px-4 rounded hover:bg-orange-700 float-end">
            {formGroup ? "Update" : "Create"}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
